import axios from 'axios';
import { cookieUtils } from 'helpers/cookiesUtils';
import logoutHandler from 'helpers/logoutHandler';

const GUEST_ID_KEY = 'guest_id';
const AUTH_ROUTES = ['admin', 'masseuse'];
const DEFAULT_LANGUAGE = 'en';

const getGuestId = () => {
    try {
        const guestId = localStorage.getItem(GUEST_ID_KEY);
        return guestId ? parseInt(guestId, 10) : null;
    } catch {
        return null;
    }
};

const getAuthHeaders = () => {
    const { getCookies } = cookieUtils;
    const cookies = getCookies();
    const jwtToken = cookies?.tokens?.accessToken;

    if (!jwtToken) return {};

    return {
        Authorization: `Bearer ${jwtToken}`,
        Language: DEFAULT_LANGUAGE,
    };
};

const isAuthRequired = (url) => {
    return AUTH_ROUTES.some((route) => url?.includes(route));
};

const parseUrlAndParams = (config) => {
    if (typeof config?.url !== 'string') return config;

    const [urlWithoutQuery, queryString] = config.url.split('?');

    if (urlWithoutQuery) {
        config.url = urlWithoutQuery;
    }

    if (queryString) {
        const urlSearchParams = new URLSearchParams(queryString);
        const params = Object.fromEntries(urlSearchParams.entries());
        config.params = { ...config.params, ...params };
    }

    return config;
};

const transformFetchParams = (params) => {
    if (params?.payload && typeof params.payload === 'object') {
        params.payload = JSON.stringify(params.payload);
    }
    return params;
};

const setContentType = (config) => {
    if (config.data instanceof FormData) {
        config.headers['Content-Type'] = 'multipart/form-data';
    }
    return config;
};

const buildHeaders = (config, isAuth, guestId) => {
    const baseHeaders = {
        'X-Guest-ID': guestId,
        ...config.headers,
    };

    if (isAuth) {
        return {
            ...getAuthHeaders(),
            ...baseHeaders,
        };
    }

    return baseHeaders;
};

const requestInterceptor = (config) => {
    const parsedConfig = parseUrlAndParams(config);
    const isAuth = isAuthRequired(parsedConfig.url);
    const guestId = getGuestId();

    const configWithContentType = setContentType(parsedConfig);

    return {
        ...configWithContentType,
        params: transformFetchParams(configWithContentType.params),
        withCredentials: true,
        headers: buildHeaders(configWithContentType, isAuth, guestId),
    };
};

const responseInterceptor = {
    onFulfilled: (response) => response,
    onRejected: (error) => {
        if (error.response?.status === 401) {
            logoutHandler();
            return null;
        }

        return Promise.reject(error);
    },
};

const createAxiosInstance = () => {
    const instance = axios.create({
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
        withCredentials: false,
    });

    instance.interceptors.request.use(requestInterceptor, Promise.reject);
    instance.interceptors.response.use(responseInterceptor.onFulfilled, responseInterceptor.onRejected);

    return instance;
};

export default createAxiosInstance();

export { getGuestId, getAuthHeaders, isAuthRequired, transformFetchParams };
